import { inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Action, ActionName } from '@app/actions/action.types';
import { getActionIcon } from '@app/actions/action-icons.util';
import { getActionRouteCommands } from '@app/actions/action-routes.util';
import { ActionSandbox } from '@app/actions/action.sandbox';
import { DynamicFlowComponent } from '@app/dynamic-flow/dynamic-flow.component';
import { SignDocumentBatchModalComponent } from '@app/document-bundle-signing/components/sign-document-batch-modal/sign-document-batch-modal.component';
import { OneOnOneChatModalComponent } from '@app/one-on-one-chat/modals/one-on-one-chat-modal/one-on-one-chat-modal.component';
import { isOneOnOneChatModalId, oneOnOneChatModalId } from '@app/one-on-one-chat/utils/one-on-one-chat-modal-id.util';

export abstract class ActionBase {
  protected readonly actionSandbox = inject(ActionSandbox);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly modalCtrl = inject(ModalController);

  protected readonly documentDownloading = this.actionSandbox.documentDownloading;

  protected readonly getActionIcon = getActionIcon;

  protected onActionClick(action: Action): void {
    const route = getActionRouteCommands(action);
    if (route) {
      this.navigateToAction(route.commands, route.queryParams);
      return;
    }
    if (action.name === ActionName.DownloadDocument) {
      this.actionSandbox.downloadDocument(action.data.documentId!);
    } else if (action.name === ActionName.OpenSurvey) {
      this.actionSandbox.openSurvey(action);
    } else if (action.name === ActionName.StartFlow) {
      this.startFlow(action.data.flowId!, action.data.externalId);
    } else if (action.name === ActionName.SignDocumentBatch) {
      this.openSignDocumentBatch(action.data.batchId!);
    } else if (action.name === ActionName.NewChatMessage) {
      this.openOneOnOneChat(action.data.chatAgentExternalId!);
    } else if (action.name === ActionName.OpenExternalLink) {
      this.actionSandbox.openExternalLink(action.data.url!);
    } else {
      this.onUnsupportedAction(action);
    }
  }

  /**
   * How route-based actions are navigated. Components navigate relative to their
   * current route; root-context subclasses (e.g. notifications) override this to
   * navigate absolutely.
   */
  protected navigateToAction(commands: string[], queryParams?: Params): void {
    this.router.navigate(commands, { relativeTo: this.route, queryParams });
  }

  /**
   * Hook for actions the base class cannot dispatch. No-op by default;
   * pages that want feedback (e.g. a toast) override this.
   */
  protected onUnsupportedAction(_action: Action): void {}

  private async startFlow(flowId: string, externalId?: string): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: DynamicFlowComponent,
      componentProps: { flowId, externalId },
    });
    modal.present();
  }

  private async openSignDocumentBatch(documentBatchId: string): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: SignDocumentBatchModalComponent,
      componentProps: { documentBatchId },
    });
    modal.present();
  }

  /**
   * An already open chat with this office stays as is: it brings in the new message itself.
   * The chat with another office is replaced.
   */
  private async openOneOnOneChat(agentExternalId: string): Promise<void> {
    const id = oneOnOneChatModalId(agentExternalId);
    const topModal = await this.modalCtrl.getTop();
    if (topModal?.id === id) {
      return;
    }
    if (topModal && isOneOnOneChatModalId(topModal.id)) {
      await topModal.dismiss();
    }
    const modal = await this.modalCtrl.create({
      component: OneOnOneChatModalComponent,
      componentProps: { agentExternalId },
      id,
    });
    modal.present();
  }
}
